import { FormEvent, useEffect, useMemo, useState } from "react";

export interface StoreOption { value: string | number; label: string }
export interface ServiceAddonFormValues {
  code: string;
  name: string;
  branchId: number;
  description?: string;
  price?: string;
  isActive: boolean;
}

interface Props {
  storeOptions: StoreOption[];
  onClose: () => void;
  onSubmit: (values: ServiceAddonFormValues) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
  titleText?: string;
  submitText?: string;
  initialValues?: Partial<ServiceAddonFormValues>;
}

export const ServiceAddonCreateModal: React.FC<Props> = ({ storeOptions, onClose, onSubmit, isSubmitting = false, titleText, submitText, initialValues }) => {
  const defaultbranchId = useMemo(
    () => (storeOptions.length > 0 ? Number(storeOptions[0].value) : 0),
    [storeOptions]
  );
  const [form, setForm] = useState<ServiceAddonFormValues>({
    code: "",
    name: "",
    branchId: defaultbranchId,
    isActive: true,
    price: "",
  });
  // set branchId mặc định khi giá trị hiện tại không nằm trong danh sách
  // (không dùng !form.branchId — 0 là "Dùng chung", một lựa chọn hợp lệ)
  useEffect(() => {
    if (storeOptions.length > 0 && !storeOptions.some((o) => Number(o.value) === Number(form.branchId))) {
      setForm((p) => ({ ...p, branchId: Number(storeOptions[0].value) }));
    }
  }, [form.branchId, storeOptions]);

  useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({
        code: initialValues.code ?? prev.code,
        name: initialValues.name ?? prev.name,
        branchId: initialValues.branchId ?? prev.branchId,
        description: initialValues.description ?? prev.description,
        price: initialValues.price ?? prev.price,
        isActive: initialValues.isActive ?? prev.isActive,
      }));
    }
  }, [initialValues]);

  const handleChange = <K extends keyof ServiceAddonFormValues>(key: K, value: ServiceAddonFormValues[K]) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  const formatMoneyInput = (raw: string): string => {
    const digits = (raw || "").toString().replace(/\D/g, "");
    if (!digits) return "";
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await onSubmit(form);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl rounded-3xl bg-white p-8 shadow-2xl">
        <div className="flex items-start justify-between">
          <h2 className="text-2xl font-semibold text-slate-800">{titleText ?? "Thêm Dịch vụ Addon"}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full text-xl text-slate-400 hover:bg-slate-100">×</button>
        </div>

        <form className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2" onSubmit={submit}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Tên dịch vụ addon <span className="text-rose-500">*</span></label>
            <input value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="Nhập Tên dịch vụ addon" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" required />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Mã addon <span className="text-rose-500">*</span></label>
            <input value={form.code} onChange={(e) => handleChange("code", e.target.value.toUpperCase())} placeholder="ADDON_CODE" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm uppercase shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" required maxLength={50} />
          </div>

          {/* Đại lý — tạm ẩn: danh mục đang dùng chung cho mọi chi nhánh, branchId lấy từ initialValues (0 = dùng chung).
              Mở lại khi cần tạo dịch vụ kèm riêng theo chi nhánh.
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Đại lý áp dụng <span className="text-rose-500">*</span></label>
            <select
              value={form.branchId}
              onChange={(e) => handleChange("branchId", Number(e.target.value))}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              required
            >
              {storeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div> */}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Đơn giá</label>
            <input value={form.price ?? ""} onChange={(e) => handleChange("price", formatMoneyInput(e.target.value))} placeholder="Nhập đơn giá" inputMode="numeric" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium text-slate-700">Mô tả</label>
            <input value={form.description ?? ""} onChange={(e) => handleChange("description", e.target.value)} placeholder="Mô tả ngắn" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>


          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="inline-flex min-w-[110px] items-center justify-center rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-emerald-600" disabled={isSubmitting}>Hủy</button>
            <button type="submit" className="inline-flex min-w-[110px] items-center justify-center rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60" disabled={isSubmitting}>
              {isSubmitting ? "Đang lưu..." : (submitText ?? "Lưu")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceAddonCreateModal;

